import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, ArrowRight, X } from 'lucide-react';

const ActionsSuggestions = ({ currentPage }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchSuggestions();
  }, [currentPage]);

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/smart-actions/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_page: currentPage,
          limit: 5
        })
      });

      const result = await response.json();
      if (result.success) {
        setSuggestions(result.data);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-red-500/30 bg-red-500/10';
      case 'medium':
        return 'border-yellow-500/30 bg-yellow-500/10';
      case 'low':
        return 'border-blue-500/30 bg-blue-500/10';
      default:
        return 'border-white/10 bg-white/5';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return '🔴';
      case 'medium':
        return '🟡';
      case 'low':
        return '🔵';
      default:
        return '⚪';
    }
  };

  const handleActionClick = (suggestion) => {
    if (suggestion.action_type === 'navigate') {
      window.location.href = suggestion.action_data.route;
    }
  };

  if (!isExpanded) {
    return (
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={() => setIsExpanded(true)}
        className="fixed bottom-6 right-6 px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-200"
        data-testid="expand-actions-button"
      >
        <Zap size={20} className="inline mr-2" />
        Actions suggérées ({suggestions.length})
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-xl rounded-2xl border border-cyan-500/20 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
          <Zap size={20} className="text-cyan-400" />
          Actions suggérées
        </h3>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsExpanded(false)}
          className="p-1 text-slate-400 hover:text-white transition-colors"
          data-testid="collapse-actions-button"
        >
          <X size={18} />
        </motion.button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400">Chargement...</div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          Aucune suggestion pour le moment
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {suggestions.map((suggestion, index) => (
              <motion.div
                key={suggestion.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleActionClick(suggestion)}
                className={`p-4 rounded-xl border cursor-pointer hover:scale-[1.02] transition-all duration-200 ${
                  getPriorityColor(suggestion.priority)
                }`}
                data-testid={`action-${suggestion.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl mt-1">{getPriorityIcon(suggestion.priority)}</span>
                  <div className="flex-1">
                    <div className="text-white font-medium mb-1">{suggestion.title}</div>
                    <div className="text-slate-300 text-sm mb-2">{suggestion.description}</div>
                    <div className="text-xs text-slate-400 italic">{suggestion.reasoning}</div>
                  </div>
                  {suggestion.action_type === 'navigate' && (
                    <ArrowRight size={18} className="text-cyan-400 mt-1 flex-shrink-0" />
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={fetchSuggestions}
        className="w-full mt-4 px-4 py-2 bg-white/5 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-white/10 transition-all duration-200 text-sm"
        data-testid="refresh-actions-button"
      >
        Actualiser les suggestions
      </motion.button>
    </motion.div>
  );
};

export default ActionsSuggestions;
